/**
 * The phone-camera scanner for QR codes and barcodes (ZAR-141).
 *
 * zxing (free, Apache-2.0) reads QR, Code 128, Code 39, EAN-13/8, UPC-A/E, ITF, Data Matrix and more, and —
 * unlike the browser's own BarcodeDetector — works on iPhone Safari too. It is imported only when somebody
 * presses "Scan with camera", so nobody else downloads it.
 *
 * Cameras need a secure page (HTTPS, or localhost). A handheld scanner needs none of this: it types the
 * code and presses Enter, which the page's code box already handles.
 *
 * Usage: <div x-data="codeScanner({ method: 'resolve' })"> … <video x-ref="video"> … </div>
 * Each code read is handed to the Livewire method named by `method` — once, not on every frame it stays in view.
 */
export function codeScanner(config = {}) {
    return {
        scanning: false,
        starting: false,
        error: '',
        cameras: [],
        cameraId: '',
        controls: null,
        last: { code: '', at: 0 },

        async start() {
            this.error = '';
            if (!window.isSecureContext) {
                this.error = 'The camera only works on a secure (https) page. Type the code or use a handheld scanner instead.';
                return;
            }
            if (!navigator.mediaDevices?.getUserMedia) {
                this.error = 'This browser can’t use the camera. Type the code or use a handheld scanner instead.';
                return;
            }
            this.starting = true;
            try {
                const { BrowserMultiFormatReader } = await import('@zxing/browser');
                const reader = new BrowserMultiFormatReader();
                if (!this.cameras.length) {
                    this.cameras = (await BrowserMultiFormatReader.listVideoInputDevices()).map((d) => ({ id: d.deviceId, label: d.label || 'Camera' }));
                    // The back camera, where there is one — it is the one pointed at things.
                    const back = this.cameras.find((c) => /back|rear|environment/i.test(c.label));
                    this.cameraId = this.cameraId || back?.id || '';
                }
                const constraints = this.cameraId
                    ? { video: { deviceId: { exact: this.cameraId } } }
                    : { video: { facingMode: 'environment' } };
                this.controls = await reader.decodeFromConstraints(constraints, this.$refs.video, (result) => {
                    if (result) {
                        this.found(result.getText());
                    }
                });
                this.scanning = true;
            } catch (e) {
                this.error = e?.name === 'NotAllowedError'
                    ? 'Camera access was refused. Allow it in the browser’s settings, or type the code.'
                    : 'The camera couldn’t start: ' + (e?.message || e);
                this.stop();
            } finally {
                this.starting = false;
            }
        },

        stop() {
            try { this.controls?.stop(); } catch (e) { /* already stopped */ }
            this.controls = null;
            this.scanning = false;
        },

        async switchCamera(id) {
            this.cameraId = id;
            if (this.scanning) {
                this.stop();
                await this.start();
            }
        },

        found(code) {
            const now = Date.now();
            // The same code held in view reads many times a second — one hand-off per three seconds.
            if (code === this.last.code && now - this.last.at < 3000) {
                return;
            }
            this.last = { code, at: now };
            if (navigator.vibrate) {
                navigator.vibrate(60);
            }
            if (config.stopOnRead !== false) {
                this.stop();
            }
            this.$wire.call(config.method || 'resolve', code);
        },

        init() {
            // Leaving the page (or a wire:navigate away) must let go of the camera.
            document.addEventListener('livewire:navigating', () => this.stop(), { once: true });
        },

        destroy() {
            this.stop();
        },
    };
}
